/**
 * Unsaved-changes guard — pure decision logic.
 *
 * Deliberately free of React and DOM so it can be unit-tested. This repository
 * has no `jsdom` and no DOM test environment, so anything that touches
 * `window` cannot be covered by an automated test here. Every decision that can
 * actually be *wrong* therefore lives in this file, and the React/DOM layer is
 * a thin, dumb shell over it.
 *
 * Why the care: a guard that fires when nothing changed, or fires after a
 * successful save, teaches users to dismiss warnings reflexively. On a
 * half-written incident report that is worse than having no guard at all. Every
 * function here exists to make a spurious prompt impossible.
 */

/** Value snapshot used for dirty comparison. */
export type DirtySnapshot = string;

/**
 * Normalises a form value for comparison.
 *
 * Trailing whitespace, `undefined` vs missing key, and key ordering must never
 * arm the guard. Without this, a form that sets `{ notes: undefined }` on mount
 * and `{}` after hydration reads as dirty before the user types anything.
 */
export function snapshot(value: unknown): DirtySnapshot {
  return JSON.stringify(normalise(value));
}

function normalise(value: unknown): unknown {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(normalise);
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Sort keys so object literal order cannot affect the comparison, and drop
    // undefined so an explicitly-undefined key equals an absent one.
    for (const key of Object.keys(source).sort()) {
      if (source[key] === undefined) continue;
      out[key] = normalise(source[key]);
    }
    return out;
  }
  return value;
}

/**
 * True when the current value differs from the baseline captured at load.
 *
 * The baseline must be the last **durably saved** state. It must NOT be
 * re-captured on autosave: `useFormDraft.isDirty` re-checkpoints on every
 * autosave write, which makes it mean "changed since last autosave" — roughly
 * 800ms of coverage — not "changed since last save". A guard built on that is
 * armed almost never.
 */
export function isDirty(current: unknown, baseline: DirtySnapshot | null): boolean {
  if (baseline === null) return false;
  return snapshot(current) !== baseline;
}

export interface GuardDecisionInput {
  /** The form holds edits not yet durably saved. */
  dirty: boolean;
  /**
   * A submit is in flight or has just succeeded and is navigating away.
   * MUST be read from a ref set synchronously before the await, not from React
   * state — batching can otherwise leave this false during the post-submit
   * `router.push`, prompting the user immediately after a successful save.
   */
  submitting: boolean;
  /** A draft-restore prompt is awaiting the user's decision. */
  restorePending?: boolean;
  /** The user has deliberately chosen to discard (delete, cancel, discard). */
  discarding?: boolean;
}

/** The single decision: should leaving this surface be confirmed? */
export function shouldConfirmLeave(input: GuardDecisionInput): boolean {
  if (!input.dirty) return false;
  if (input.submitting) return false;
  if (input.discarding) return false;
  if (input.restorePending) return false;
  return true;
}

/**
 * True when navigating from `currentHref` to `href` leaves the current page.
 *
 * A same-page hash change, an identical URL, a download, an external origin, or
 * a `mailto:`/`tel:` link is not a departure and must never prompt. Mirrors the
 * `isMeaningfulNav` logic in `route-progress-bar.tsx` deliberately — the two
 * must agree, or the progress bar will start for a navigation the guard blocks.
 */
export function isLeavingNavigation(href: string, currentHref: string): boolean {
  let target: URL;
  let current: URL;
  try {
    current = new URL(currentHref);
    target = new URL(href, currentHref);
  } catch {
    // An unparsable href is not a navigation we can reason about. Do not block
    // it — a guard that blocks what it does not understand is a broken app.
    return false;
  }
  if (target.protocol !== 'http:' && target.protocol !== 'https:') return false;
  if (target.origin !== current.origin) return false;
  return target.pathname !== current.pathname || target.search !== current.search;
}

/**
 * Whether a click event should be considered a candidate in-app navigation.
 * Modifier-clicks, middle-clicks, downloads and new-tab targets open elsewhere
 * and leave the form intact, so they must not prompt.
 */
export interface ClickLike {
  defaultPrevented: boolean;
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

export interface AnchorLike {
  href: string;
  target?: string | null;
  hasDownload?: boolean;
}

export function isGuardableAnchorClick(event: ClickLike, anchor: AnchorLike | null): boolean {
  if (anchor === null) return false;
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.hasDownload) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  return true;
}

/**
 * Event the guard dispatches when it cancels a navigation.
 *
 * `route-progress-bar.tsx` starts its bar from a capture-phase click listener
 * on `document`. If the guard cancels the navigation afterwards, that bar hangs
 * until its 12s safety ceiling. The bar already listens for this exact event to
 * reset itself, so cancelling MUST dispatch it. Registration order between the
 * two capture listeners is not guaranteed, so we cannot rely on
 * `defaultPrevented` alone.
 */
export const ROUTE_PROGRESS_CANCEL_EVENT = 'statura:route-progress-cancel';

export const DEFAULT_UNSAVED_MESSAGE =
  'You have unsaved changes. If you leave this page they will be lost.';
